/*
 * Boot-failure screen.
 *
 * This file used to be 491 lines: it built a campfire loading screen as an
 * innerHTML string with its own inlined stylesheet, preloaded a Google font
 * and a 68 KB PNG at import time, ran two intervals spawning DOM particles,
 * and held the screen open for a mandatory three seconds even when the app was
 * already ready. A dead Vue component (RPGLoader.vue) was a second copy of the
 * same screen.
 *
 * The loading screen now lives inline in index.html, so it paints on the first
 * byte of the document instead of after the bundle parses, and Vue's mount
 * into #app removes it. What remains here is only the case Vue cannot handle —
 * the app failing to start at all, when no component will ever render.
 */

const BOOT_STYLE_ID = 'dx-boot-error-style';

const ERROR_CSS = `
  .dx-boot-error {
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 1.5rem;
    text-align: center;
    background: #080b10;
    z-index: 100;
  }

  .dx-boot-error__title {
    font-family: 'Orbitron', system-ui, sans-serif;
    font-size: 1.25rem;
    letter-spacing: 0.04em;
    color: #ff8f8f;
    margin: 0;
  }

  .dx-boot-error__message {
    font-family: 'IBM Plex Sans', system-ui, sans-serif;
    font-size: 1rem;
    line-height: 1.65;
    color: #a7bcb9;
    max-width: 54ch;
    margin: 0;
  }

  .dx-boot-error__retry {
    margin-top: 0.5rem;
    padding: 0.7rem 1.4rem;
    font-family: 'IBM Plex Mono', ui-monospace, monospace;
    font-size: 0.8125rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #00ffc8;
    background: rgba(0, 255, 200, 0.1);
    border: 1px solid rgba(0, 255, 200, 0.34);
    border-radius: 8px;
    cursor: pointer;
  }

  .dx-boot-error__retry:hover {
    background: rgba(0, 255, 200, 0.18);
  }
`;

/**
 * Replaces the boot screen with a failure state the user can act on.
 *
 * @param {string} elementId - id of the element to render into
 * @param {string} message - what failed, in the user's terms
 */
export function showError(elementId = 'app', message = 'Failed to load application.') {
  const element = document.getElementById(elementId);
  if (!element) return;

  if (!document.getElementById(BOOT_STYLE_ID)) {
    const style = document.createElement('style');
    style.id = BOOT_STYLE_ID;
    style.textContent = ERROR_CSS;
    document.head.appendChild(style);
  }

  element.textContent = '';

  const overlay = document.createElement('div');
  overlay.className = 'dx-boot-error';
  overlay.setAttribute('role', 'alert');

  const title = document.createElement('p');
  title.className = 'dx-boot-error__title';
  title.textContent = 'The rift did not open';

  /* textContent, not innerHTML: `message` can carry an error string from a
   * failed response, and the old template interpolated it straight into markup. */
  const body = document.createElement('p');
  body.className = 'dx-boot-error__message';
  body.textContent = message;

  const retry = document.createElement('button');
  retry.className = 'dx-boot-error__retry';
  retry.type = 'button';
  retry.textContent = 'Try again';
  retry.addEventListener('click', () => window.location.reload());

  overlay.append(title, body, retry);
  element.appendChild(overlay);
}
